import {Component, EventEmitter, Input, Output, inject} from '@angular/core';
import {CommonModule} from "@angular/common";
import {SidebarButtonComponent} from "../_components/sidebar-button/sidebar-button.component";
import {EmployeePanelRoutes} from "../employee-panel-routes";
import {EventType, Router, RouterLink} from "@angular/router";
import {AuthenticationService} from "../../../_auth/authentication.service";
import {ContextSwitcherPresenceService} from "../../../_auth/context/context-switcher-presence.service";
import {ContextOptionsStore} from "../../../_auth/context/context-options.store";

/**
 * The employee panel's left-hand navigation.
 *
 * ### Reserving space for the floating context switcher (#575)
 *
 * `<auth-context-switcher>` is a single, app-wide overlay pinned `fixed bottom-4 left-4` on every shell (see its
 * own remarks) — it takes part in no host's layout, this sidebar's included. Left alone, its pill sits directly
 * on top of the BENUTZER group's last entries, hiding "Ausloggen" behind it at every width the sidebar reaches
 * the bottom of the viewport (which, below `lg`, is always — the sidebar is `p-3`/`absolute inset-0`).
 *
 * The fix is a reserved `padding-bottom` on that bottom group, applied ONLY while the switcher is actually on
 * screen — {@link switcherVisible}. Moving the switcher into this sidebar's own flow instead was the other
 * option the bug named, and it does not fit this codebase's shape: the switcher is ONE global instance shared
 * by three structurally different shells (this sidebar, the operator rail, the management top nav has none at
 * all), rendered once in `app.component.html` — turning it into a per-shell element would mean re-deriving its
 * fetch-and-render logic three times, which is the exact duplication `ContextSwitcherPresenceService` (and the
 * central booking calculator, the central role vocabulary, … elsewhere in this codebase) exists to avoid.
 * Reserving space is a one-line, purely presentational answer to a purely presentational overlap.
 *
 * On a phone the gap is now gone again, and correctly so: since #602 the switcher renders as a chip in the top
 * bar below Tailwind's `sm` and nothing floats over this drawer at all, so {@link switcherVisible} reports
 * `false` there. The reservation stays for the widths where the pill really is in the corner.
 *
 * ### The drawer head (#602)
 *
 * The employee panel's top bar used to read „CheqMe: &lt;Name&gt;". The context chip took that slot, so the
 * person's name moved to the top of this drawer — same link to **Mein Profil**, now with the current
 * Arbeitsbereich named underneath it. The label comes from {@link ContextOptionsStore}, the same single fetch
 * the switcher itself reads: this drawer must never ask `/auth/context/available` a second time to print a
 * name the app already knows.
 */
@Component({
  selector: 'app-sidebar',
  imports: [
    CommonModule,
    SidebarButtonComponent,
    RouterLink
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent {
  private readonly presence = inject(ContextSwitcherPresenceService);
  private readonly contextOptions = inject(ContextOptionsStore);

  constructor(public AuthenticationService: AuthenticationService, private router: Router) {
    this.router.events.subscribe({
      next: (e) => {
        if (e.type == EventType.NavigationEnd) {
          this.open = false;
          this.openChange.emit(this.open);
        }
      }
    })
  }

  @Input() open: boolean = false;
  @Output() openChange = new EventEmitter<boolean>();

  protected readonly EmployeePanelRoutes = EmployeePanelRoutes;

  /** Whether the floating context switcher pill is currently on screen — see the class remarks. */
  protected readonly switcherVisible = this.presence.visible;

  /** The current Arbeitsbereich, shown under the name in the drawer head, or null when there is none to name. */
  protected readonly contextLabel = this.contextOptions.currentLabel;

  /**
   * The signed-in person's name, as the top bar used to print it.
   *
   * The fallback is „Mitarbeiter" — the same word the bar used — rather than an empty line, because the head is
   * a link and a link with no text is a link nobody can press.
   */
  protected get userName(): string {
    return this.AuthenticationService.userValue?.name ?? 'Mitarbeiter';
  }
}
